import _ from 'lodash';
import {
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ForwardedRef,
} from 'react';
import { fetchHolterEventsDailyCount } from '@/entities/event';
import { KEY_CANCEL, KEY_RECOIL, typesPersistEventV2 } from '@/shared/config';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { useUpdateEffect } from '@/shared/lib/customHooks';
import { logError } from '@/shared/lib/apiHelpers';
import {
  selectIsEmptyData,
  selectIsLoadingHrChart,
  selectJumpDate,
  selectSelectedDateValue,
  useReportStore,
} from '@/entities/report';
import type { HrChartOptionsHandle, HrChartOptionsProps } from './index';

/**
 * Drives the date selector above the ECG disclosure heart-rate chart on report tab 3. It loads the holter daily event counts once, builds MM/DD labels annotated with each day's count or No data, keeps the selected date, index and empty-data flag in the report store, honours an incoming jump date or a reload triggered through the imperative handle, and exposes the arrow and dropdown handlers.
 *
 * @param props - Chart options props with study and profile ids, report window and timezone offset.
 * @param ref - Forwarded handle exposing reload to the parent.
 * @returns An object with the loading flags, current date index, daily counts, dropdown labels, selected label, change and arrow handlers and the empty-data flag.
 */
const useHrChartOptions = (
  { timezoneOffset, studyId, profileId, startReport, stopReport }: HrChartOptionsProps,
  ref: ForwardedRef<HrChartOptionsHandle>,
) => {
  const keyRecoil = KEY_RECOIL.STRIP_MANAGEMENT;

  const selectedDateValue = useReportStore(selectSelectedDateValue(keyRecoil));
  const setSelectedDateValueAction = useReportStore((s) => s.actions.setSelectedDateValue);
  /**
   * Writes the selected ECG disclosure date for report tab 3 into the shared report store. It calls the store action with the tab's key bound in front of the caller's value.
   *
   * @param v - New value or updater for the selected date.
   * @returns Nothing.
   */
  const setSelectedDateValue = (v: Parameters<typeof setSelectedDateValueAction>[1]) =>
    setSelectedDateValueAction(keyRecoil, v);
  const isEmptyData = useReportStore(selectIsEmptyData(keyRecoil));
  const setIsEmptyDataAction = useReportStore((s) => s.actions.setIsEmptyData);
  /**
   * Writes the empty-data flag for report tab 3 into the shared report store. It calls the store action with the tab's key bound in front of the caller's value.
   *
   * @param v - New value or updater for the empty-data flag.
   * @returns Nothing.
   */
  const setIsEmptyData = (v: Parameters<typeof setIsEmptyDataAction>[1]) =>
    setIsEmptyDataAction(keyRecoil, v);
  const jumpDate = useReportStore(selectJumpDate(keyRecoil));
  const setJumpDateAction = useReportStore((s) => s.actions.setJumpDate);
  /**
   * Writes the pending jump date for report tab 3 into the shared report store. It calls the store action with the tab's key bound in front of the caller's value.
   *
   * @param v - New value or updater for the jump date.
   * @returns Nothing.
   */
  const setJumpDate = (v: Parameters<typeof setJumpDateAction>[1]) =>
    setJumpDateAction(keyRecoil, v);
  const ecgDisclosure = useReportStore((s) => s.selectedEcgDisclosure);
  const isLoadingHrChart = useReportStore(selectIsLoadingHrChart(keyRecoil));
  const ecgDisclosureChange = useReportStore((s) => s.ecgDisclosureChange);

  const [isLoadingHrChartOptions, setIsLoadingHrChartOptions] = useState(true);
  const [dailyCount, setDailyCount] = useState([]);
  const [dateIndex, setDateIndex] = useState(0);

  const isReload = useRef(false);
  const isLoaded = useRef(false);

  const selectedEcgDisclosure = useMemo(() => {
    if (!_.isEmpty(ecgDisclosureChange)) return ecgDisclosureChange;
    return ecgDisclosure;
  }, [ecgDisclosure, ecgDisclosureChange]);

  const dropdownOptions = useMemo(
    () =>
      _.map(
        dailyCount,
        (x) =>
          `${convertToMomentJs(x.date, timezoneOffset).format('MM/DD')} ${x.count === -1 ? '(No data)' : `(${x.count})`}`,
      ),
    [dailyCount],
  );

  const dateList = useMemo(
    () => _.map(dailyCount, (x) => convertToMomentJs(x.date, timezoneOffset)),
    [dailyCount],
  );

  /**
   * Applies a day picked from the date dropdown to the ECG disclosure chart. It resolves the label back to its index, converts that day to an ISO string at the study timezone offset, and only when it differs from the current selection clears the jump date, moves the index and updates the selected date and empty-data flag.
   *
   * @param name - Field name reported by the dropdown.
   * @param value - Selected dropdown label.
   * @returns Nothing.
   */
  const onChangeDate = (name: string, value: string) => {
    const index = _.findIndex(dropdownOptions, (x) => x === value);
    if (index > -1) {
      const selectedDateMoment = convertToMomentJs(dailyCount[index].date, timezoneOffset);
      const changedSelectedDateValue = selectedDateMoment.toISOString();
      if (changedSelectedDateValue !== selectedDateValue) {
        setJumpDate(undefined);
        setDateIndex(index);
        setSelectedDateValue(changedSelectedDateValue);
        setIsEmptyData(dailyCount[index].count === -1);
      }
    }
  };

  /**
   * Steps the ECG disclosure date one day forward or backward. It clears any pending jump date, moves the index, pushes the neighbouring day's date into the store and marks the day empty when its count is -1.
   *
   * @param isRight - True to move to the next day, false for the previous one.
   * @returns Nothing.
   */
  const handleClickArrow = (isRight: boolean) => {
    const newDateIndex = isRight ? dateIndex + 1 : dateIndex - 1;
    setJumpDate(undefined);
    setDateIndex(newDateIndex);
    setSelectedDateValue(dailyCount[newDateIndex].date);
    setIsEmptyData(dailyCount[newDateIndex].count === -1);
  };

  /**
   * Loads the per-day event counts that drive the ECG disclosure date picker. It raises the options loading flag, queries the holter daily counts for the study, profile and report window under the holter AI cancel key, stores the result, and logs and swallows any failure before clearing the flag.
   *
   * @returns A promise resolving once the daily counts are stored and the loading flag cleared.
   */
  const handleFetchHolterEventsDailyCountV2 = async () => {
    setIsLoadingHrChartOptions(true);
    try {
      const holterEventsDailyCountFilter = {
        studyId,
        profileId,
        types: typesPersistEventV2,
        startSearchDate: startReport,
        stopSearchDate: stopReport,
      };
      const holterEventsDailyCount = await fetchHolterEventsDailyCount(
        holterEventsDailyCountFilter,
        KEY_CANCEL.API_HOLTER_AI,
      );
      setDailyCount(holterEventsDailyCount);
    } catch (error) {
      logError('Failed to fetch holter events daily count: ', error);
    }
    setIsLoadingHrChartOptions(false);
  };

  useEffect(() => {
    if (selectedDateValue) {
      const findIndex = _.findIndex(dailyCount, (x) => x.date === selectedDateValue);
      setIsEmptyData(dailyCount?.[findIndex]?.count === -1);
    }
  }, [selectedDateValue, dailyCount]);

  useEffect(() => {
    handleFetchHolterEventsDailyCountV2();
  }, []);

  useUpdateEffect(() => {
    if (!_.isEmpty(dailyCount)) {
      if (jumpDate) {
        const date = convertToMomentJs(jumpDate, timezoneOffset);
        const dateString = date.format('MM/DD');
        const index = _.findIndex(dateList, (x) => x.format('MM/DD') === dateString);
        if (index > -1) {
          setDateIndex(index);
          setSelectedDateValue(dateList[index].toISOString());
        }
        isLoaded.current = true;
      } else if (isReload.current) {
        isReload.current = false;
        const date = convertToMomentJs(selectedEcgDisclosure.start, timezoneOffset);
        const dateString = date.format('MM/DD');
        const index = _.findIndex(dateList, (x) => x.format('MM/DD') === dateString);
        if (index > -1) {
          setDateIndex(index);
          setSelectedDateValue(dateList[index].toISOString());
          setJumpDate(date.valueOf());
        }
      } else if (!isLoaded.current) {
        setDateIndex(0);
        setSelectedDateValue(dateList[0].toISOString());
      }
    }
  }, [dailyCount]);

  useUpdateEffect(() => {
    if (!_.isEmpty(dailyCount)) {
      if (jumpDate) {
        const date = convertToMomentJs(jumpDate, timezoneOffset);
        const dateString = date.format('MM/DD');
        const index = _.findIndex(dateList, (x) => x.format('MM/DD') === dateString);
        if (index > -1) {
          setDateIndex(index);
          setSelectedDateValue(dateList[index].toISOString());
        }
      }
    }
  }, [dailyCount, jumpDate]);

  useImperativeHandle(ref, () => ({
    reload: () => {
      isReload.current = true;
      handleFetchHolterEventsDailyCountV2();
    },
  }));

  const formatSelectedDate = dropdownOptions[dateIndex];

  return {
    isLoadingHrChartOptions,
    isLoadingHrChart,
    dateIndex,
    dailyCount,
    dropdownOptions,
    formatSelectedDate,
    onChangeDate,
    handleClickArrow,
    isEmptyData,
  };
};

export default useHrChartOptions;
